import React, { useState } from 'react';
import { Button } from '../Button';
import { GlobeAltIcon, EditIcon, DeleteIcon, PlusIcon, SaveIcon } from './DashboardIcons';

interface Delegation {
    id: number;
    country: string;
    cities: string[];
    flag: string;
}

const initialDelegations: Delegation[] = [
    { id: 1, country: 'Sede Central', cities: ['Andorra la Vella'], flag: '🇦🇩' },
    { id: 2, country: 'España', cities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao', 'Zaragoza'], flag: '🇪🇸' },
    { id: 3, country: 'Francia', cities: ['París', 'Marsella', 'Lyon', 'Niza', 'Toulouse'], flag: '🇫🇷' },
];

const emptyDelegation: Delegation = { id: 0, country: '', cities: [''], flag: '' };

export const DelegationsView: React.FC = () => {
    const [delegations, setDelegations] = useState<Delegation[]>(initialDelegations);
    const [editingDelegation, setEditingDelegation] = useState<Delegation | null>(null);

    const handleEdit = (delegation: Delegation | null) => {
        setEditingDelegation(delegation ? { ...delegation, cities: [...delegation.cities] } : { ...emptyDelegation, id: Date.now() });
    };

    const handleSave = () => {
        if (!editingDelegation) return;
        const finalDelegation = { ...editingDelegation, cities: editingDelegation.cities.filter(c => c.trim() !== '') };

        if (delegations.some(d => d.id === finalDelegation.id)) {
            setDelegations(delegations.map(d => d.id === finalDelegation.id ? finalDelegation : d));
        } else {
            setDelegations([...delegations, finalDelegation]);
        }
        setEditingDelegation(null);
    };
    
    const handleDelete = (id: number) => {
        if(window.confirm('¿Seguro que quieres eliminar esta delegación?')) {
            setDelegations(delegations.filter(d => d.id !== id));
        }
    };

    const handleCityChange = (index: number, value: string) => {
        if (!editingDelegation) return;
        const newCities = [...editingDelegation.cities];
        newCities[index] = value;
        setEditingDelegation({ ...editingDelegation, cities: newCities });
    };
    
    const addCityInput = () => {
         if (!editingDelegation) return;
         setEditingDelegation({ ...editingDelegation, cities: [...editingDelegation.cities, ''] });
    };

    const removeCityInput = (index: number) => {
        if (!editingDelegation || editingDelegation.cities.length <= 1) return;
        const newCities = editingDelegation.cities.filter((_, i) => i !== index);
        setEditingDelegation({ ...editingDelegation, cities: newCities });
    };

    return (
        <div className="p-8 bg-white rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center"><GlobeAltIcon /><span className="ml-2">Gestión de Delegaciones</span></h1>
                <Button onClick={() => handleEdit(null)} variant="primary" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <span className="flex items-center"><PlusIcon /><span className="ml-2">Añadir Delegación</span></span>
                </Button>
            </div>

            {editingDelegation && (
                <div className="bg-gray-50 p-6 rounded-lg mb-6 border">
                    <h2 className="text-xl font-semibold mb-4">Editando Delegación</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input type="text" placeholder="País" value={editingDelegation.country} onChange={e => setEditingDelegation({...editingDelegation, country: e.target.value})} className="w-full p-2 border rounded" />
                        <input type="text" placeholder="Emoji Bandera" value={editingDelegation.flag} onChange={e => setEditingDelegation({...editingDelegation, flag: e.target.value})} className="w-full p-2 border rounded" />
                    </div>
                    <div className="mt-4">
                        <h3 className="font-medium mb-2">Ciudades</h3>
                        <div className="space-y-2">
                        {editingDelegation.cities.map((city, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <input type="text" placeholder={`Ciudad ${index + 1}`} value={city} onChange={e => handleCityChange(index, e.target.value)} className="w-full p-2 border rounded" />
                                <Button onClick={() => removeCityInput(index)} variant="outline" className="text-red-500 border-red-300 hover:bg-red-50 p-2 h-10 w-10"><DeleteIcon /></Button>
                            </div>
                        ))}
                        </div>
                        <Button onClick={addCityInput} size="small" className="mt-2 bg-gray-200 text-gray-700 hover:bg-gray-300">Añadir Ciudad</Button>
                    </div>
                     <div className="flex justify-end gap-3 mt-4">
                        <Button onClick={() => setEditingDelegation(null)} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-200">Cancelar</Button>
                        <Button onClick={handleSave} variant="success"><SaveIcon /> <span className="ml-1">Guardar</span></Button>
                    </div>
                </div>
            )}
            
            <div className="space-y-4">
                {delegations.map(del => (
                    <div key={del.id} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                        <div>
                            <h3 className="font-bold text-lg">{del.flag} {del.country}</h3>
                            <p className="text-sm text-gray-600">{del.cities.join(', ')}</p>
                        </div>
                        <div className="flex space-x-2">
                             <button onClick={() => handleEdit(del)} className="text-yellow-500 hover:text-yellow-700"><EditIcon /></button>
                             <button onClick={() => handleDelete(del.id)} className="text-red-500 hover:text-red-700"><DeleteIcon /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};